import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const NODES = [
  { name: "United States", role: "Headquarters", x: 22, y: 45, size: 12 },
  { name: "Jordan", role: "Silent Partner · Janain Al-Salsabeel", x: 58, y: 47, size: 10 },
  { name: "Iraq", role: "Security Operations", x: 60, y: 43, size: 10 },
  { name: "Beijing", role: "EPC Partner · BPEC", x: 80, y: 40, size: 10 },
  { name: "West Africa", role: "Aviation & Logistics", x: 47, y: 62, size: 8 },
];

const CONNECTIONS = [
  [0, 1], [0, 2], [1, 2], [1, 3], [1, 4], [0, 3],
];

export default function GlobalReach() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotate = useTransform(scrollYProgress, [0, 1], [-2, 2]);

  return (
    <section
      id="reach"
      ref={ref}
      className="relative py-32 md:py-40 border-t border-navy-border overflow-hidden"
      data-testid="global-reach"
    >
      {/* Ambient radial */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 50% at 65% 45%, rgba(212,175,55,0.05) 0%, rgba(212,175,55,0) 60%)",
        }}
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-10 bg-gold" />
            <span className="overline">Global Reach</span>
          </div>
          <h2 className="h-display text-4xl md:text-5xl lg:text-6xl leading-[1.03]">
            Anchored in the US.<br />
            <span className="text-gold">Present where it counts.</span>
          </h2>
          <p className="mt-8 text-white/60 leading-[1.8] max-w-md">
            Mends International Group operates from the United States with
            long-standing silent partnerships in Jordan and Beijing, and active
            operational footprint across Iraq and the wider MENA region.
          </p>

          <div className="mt-12 flex flex-col divide-y divide-navy-border border-y border-navy-border">
            {NODES.map((n, i) => (
              <div
                key={n.name}
                className="flex items-center justify-between py-4"
                data-testid={`reach-node-${i}`}
              >
                <div>
                  <div className="font-display text-xl">{n.name}</div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1">
                    {n.role}
                  </div>
                </div>
                <span className="font-mono text-[10px] text-gold/70">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8 relative">
          <motion.div
            style={{ rotate }}
            className="relative aspect-[16/10] w-full border border-navy-border bg-navy-surface/40 overflow-hidden"
          >
            {/* Latitude / longitude grid */}
            <svg
              viewBox="0 0 100 62"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                <radialGradient id="mendGold" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* Grid lines */}
              {[...Array(11)].map((_, i) => (
                <line
                  key={"h" + i}
                  x1="0" x2="100"
                  y1={(i * 62) / 10} y2={(i * 62) / 10}
                  stroke="#1A253D" strokeWidth="0.15"
                />
              ))}
              {[...Array(17)].map((_, i) => (
                <line
                  key={"v" + i}
                  y1="0" y2="62"
                  x1={(i * 100) / 16} x2={(i * 100) / 16}
                  stroke="#1A253D" strokeWidth="0.15"
                />
              ))}

              {/* Connection arcs */}
              {CONNECTIONS.map(([a, b], i) => {
                const A = NODES[a];
                const B = NODES[b];
                const mx = (A.x + B.x) / 2;
                const my = Math.min(A.y, B.y) - 8;
                return (
                  <motion.path
                    key={"conn" + i}
                    d={`M ${A.x} ${A.y} Q ${mx} ${my} ${B.x} ${B.y}`}
                    stroke="#D4AF37"
                    strokeOpacity="0.4"
                    strokeWidth="0.2"
                    fill="none"
                    strokeDasharray="0.6 0.6"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 0.5 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.6, delay: 0.4 + i * 0.15, ease: "easeInOut" }}
                  />
                );
              })}

              {/* Nodes */}
              {NODES.map((n, i) => (
                <g key={n.name}>
                  <motion.circle
                    cx={n.x} cy={n.y} r={n.size / 8}
                    fill="url(#mendGold)"
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1, duration: 0.6 }}
                  />
                  <motion.circle
                    cx={n.x} cy={n.y} r="0.7"
                    fill="#D4AF37"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}
                  />
                  <motion.circle
                    cx={n.x} cy={n.y} r="1.4"
                    fill="none"
                    stroke="#D4AF37"
                    strokeWidth="0.15"
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: [0.6, 1.6, 0.6], opacity: [0.7, 0, 0.7] }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.4 }}
                  />
                  <text
                    x={n.x + 2} y={n.y - 1}
                    fill="#F8FAFC"
                    fontSize="1.1"
                    fontFamily='"IBM Plex Mono", monospace'
                    letterSpacing="0.15"
                  >
                    {n.name.toUpperCase()}
                  </text>
                </g>
              ))}
            </svg>

            {/* Corner markers */}
            {[
              "top-4 left-4", "top-4 right-4",
              "bottom-4 left-4", "bottom-4 right-4",
            ].map((pos, i) => (
              <div
                key={i}
                className={`absolute ${pos} w-3 h-3 border-t border-l border-gold/70`}
                style={{
                  transform:
                    i === 1 ? "rotate(90deg)" :
                    i === 2 ? "rotate(-90deg)" :
                    i === 3 ? "rotate(180deg)" : "none",
                }}
              />
            ))}
            <div className="absolute top-4 left-6 font-mono text-[10px] tracking-[0.3em] text-white/40">
              MIG · GLOBAL NETWORK
            </div>
            <div className="absolute bottom-4 right-6 font-mono text-[10px] tracking-[0.3em] text-gold/70">
              LIVE
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
